import { createFileRoute, Link } from "@tanstack/react-router";
import type { CSSProperties } from "react";
import { ArrowRight } from "lucide-react";

import { SiteFooter, SiteHeader } from "@/components/layout/SiteChrome";
import { Button } from "@/components/ui/button";
import { useReveal } from "@/hooks/use-reveal";
import {
  PACK_CREDIT_NOTE,
  PACK_EMAIL_URL,
  PACK_FIVE,
  PACK_NAME,
  PACK_ONE,
  PACK_PLANS,
  PACK_WHATSAPP_URL,
  type PackPlan,
} from "@/lib/listing-pack";
import { AD_SPEND_NOTE, LAUNCH_OFFER, payLabel } from "@/lib/pricing";

const BASE_URL = "https://adkiro.com";
const PAGE_URL = `${BASE_URL}/listing-creative-pack`;
const TITLE = `${PACK_NAME}: Four Ads for One Property Listing | Adkiro`;
const DESCRIPTION = `Send one listing and get four ads back in three working days, each built on a different idea, made from your own photos with the price in plain sight. ${PACK_ONE.price} for one listing, ${PACK_FIVE.price} for five.`;

const INCLUDED = [
  {
    title: "Four ad designs",
    copy: "Four different ideas for the same listing. Each one in two sizes: feed (4:5) and story (9:16).",
  },
  {
    title: "Two short videos",
    copy: "8 to 10 seconds, vertical. Motion made from your own photos: slow moves and text, no generated scenes.",
  },
  {
    title: "Ad texts",
    copy: "Three texts and three headlines, ready to paste into Ads Manager.",
  },
  {
    title: "One round of changes",
    copy: "Tell us what to change and the ads come back. The files are yours, to use anywhere.",
  },
];

type IdeaKind = "price" | "view" | "detail" | "facts";

const IDEAS: { kind: IdeaKind; title: string; copy: string }[] = [
  {
    kind: "price",
    title: "The price first",
    copy: "The number is the headline. The buyers who can pay it stop, and the rest scroll on.",
  },
  {
    kind: "view",
    title: "The view first",
    copy: "One photograph, full frame, and one line. For the listing whose best argument is the window.",
  },
  {
    kind: "detail",
    title: "One detail",
    copy: "A door, a terrace, a key. One close look that makes a buyer want to see the rest.",
  },
  {
    kind: "facts",
    title: "The facts",
    copy: "The plan and the numbers, for the buyer who is comparing three listings tonight.",
  },
];

const STEPS = [
  {
    title: "Send the listing",
    copy: "The link, or the photos and the facts: the price, the size, the rooms and the floor. For Dubai, the permit number too. Large files are easiest as a Google Drive, Dropbox or WeTransfer link.",
  },
  {
    title: "Get it in writing",
    copy: "The price and the delivery date, within one business day. No call.",
  },
  {
    title: "Pay online and start",
    copy: "By card, Apple Pay or Google Pay through Stripe, with the link in the confirmation or with the pay buttons above. The three working days start when the photos arrive.",
  },
];

const NEVER = [
  "Never a room or a view that is not in your photos.",
  "Never a price or a fact you did not give us.",
  "Never a promise of results.",
  "Anything made with AI is a prop, never the property, and the ad says so.",
];

const FAQS = [
  {
    q: "What do you need from us?",
    a: "At least eight photos of the property, the best you have; the price; the facts a buyer asks for (size, rooms, floor, completion); your logo and how buyers should reach you. For a Dubai listing, the permit number and its QR code as well.",
  },
  {
    q: "How do we send large files, and how do the ads come back?",
    a: "Email stops at about 20 MB, so photos and any video are easiest as a Google Drive, Dropbox or WeTransfer link. On WhatsApp, send photos as documents so they are not compressed. The finished pack comes back as one download link: the four ads in both sizes, the two videos and the texts.",
  },
  {
    q: "Do you change the photos?",
    a: "Light and colour are corrected, as a photographer would. Rooms, views, furniture and finishes stay exactly as they are in your photos. Nothing is added to the property and nothing is removed from it.",
  },
  {
    q: "What if we want something changed?",
    a: "One round of changes is included. Send them in one message and the ads come back within two working days.",
  },
  {
    q: "Do you run the ads too?",
    a: `Not in the pack: you get the files and the texts, and you or your agency publish them. If you want the campaign as well, the ${LAUNCH_OFFER.name} is ${LAUNCH_OFFER.price} and includes the pack, the setup in your own ad account and 30 days of management. ${PACK_CREDIT_NOTE}`,
  },
  {
    q: "How do we pay?",
    a: "Online, through Stripe: by card, Apple Pay or Google Pay, from any country. Use the pay button beside each price, or the payment link Kristian sends with the written confirmation of the price and the delivery date. After paying you are taken to a page that asks for the photos and the facts. Prices are in US dollars.",
  },
  {
    q: "Who owns the files?",
    a: "You do. Use them on Instagram, Facebook, the portals, your website or in print, for as long as you like.",
  },
  {
    q: "Do we need to get on a call?",
    a: "No. Adkiro works in writing: you send the listing by WhatsApp or email, and the confirmation, the ads and the changes all come back the same way.",
  },
];

export const Route = createFileRoute("/listing-creative-pack")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: PAGE_URL },
      { property: "og:type", content: "website" },
      { property: "og:image", content: `${BASE_URL}/logo.png?v=20260907` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              "@id": `${PAGE_URL}#service`,
              name: PACK_NAME,
              serviceType: "Advertising creative for property listings",
              url: PAGE_URL,
              provider: { "@id": `${BASE_URL}/#organization` },
              areaServed: ["United Arab Emirates", "Europe", "United States"],
              description:
                "Four ad designs, two short videos and ad texts for one property listing, made from the client's own photos and delivered in three working days.",
              offers: PACK_PLANS.map((plan) => ({
                "@type": "Offer",
                name: `${PACK_NAME}: ${plan.name}`,
                price: plan.schemaPrice,
                priceCurrency: "USD",
                description: plan.features.join(". "),
                url: `${PAGE_URL}#prices`,
              })),
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${BASE_URL}/` },
                { "@type": "ListItem", position: 2, name: PACK_NAME, item: PAGE_URL },
              ],
            },
            {
              "@type": "FAQPage",
              mainEntity: FAQS.map((faq) => ({
                "@type": "Question",
                name: faq.q,
                acceptedAnswer: { "@type": "Answer", text: faq.a },
              })),
            },
          ],
        }),
      },
    ],
  }),
  component: ListingCreativePackPage,
});

/** A stagger for things that arrive together: each a beat after the one before. */
const after = (seconds: number) => ({ "--d": `${seconds}s` }) as CSSProperties;

/** The two ways to send a listing. WhatsApp first: it is where business in the Gulf starts. */
function SendListing({ className = "" }: { className?: string }) {
  return (
    <div className={`pk-send ${className}`}>
      <Button variant="brand" size="xl" className="w-full sm:w-auto" asChild>
        <a href={PACK_WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
          Send your listing on WhatsApp <ArrowRight className="size-4" />
        </a>
      </Button>
      <a href={PACK_EMAIL_URL} className="hs-ghost w-full sm:w-auto">
        Or send it by email
      </a>
    </div>
  );
}

/**
 * One of the four kinds of idea, drawn as a sketch rather than shown as a
 * finished ad: a pack is made from the client's own photos, and there is no
 * client's photo to show until the first one arrives. The hatched areas are
 * where it goes, and the dashed square is the space kept for the permit QR
 * code on a Dubai listing.
 */
function IdeaSketch({ kind }: { kind: IdeaKind }) {
  const qr = (x: number, y: number) => (
    <g className="pk-qr">
      <rect x={x} y={y} width="26" height="26" />
      <text x={x + 13} y={y + 16.5} textAnchor="middle">
        QR
      </text>
    </g>
  );
  const photo = (label: string, x: number, y: number) => (
    <text className="pk-tag" x={x} y={y} textAnchor="middle">
      {label}
    </text>
  );

  const hatch = `url(#pk-hatch-${kind})`;

  return (
    <svg className="pk-sketch" viewBox="0 0 200 250" role="img" aria-label={`Sketch: ${kind}`}>
      <defs>
        <pattern
          id={`pk-hatch-${kind}`}
          width="7"
          height="7"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <rect className="pk-hatch-ground" width="7" height="7" />
          <line className="pk-hatch-line" x1="0" y1="0" x2="0" y2="7" />
        </pattern>
      </defs>
      <rect className="pk-paper" width="200" height="250" />
      {kind === "price" && (
        <>
          <rect fill={hatch} width="200" height="132" />
          {photo("YOUR PHOTO", 100, 70)}
          <text className="pk-big" x="16" y="178">
            AED 4.2M
          </text>
          <rect className="pk-line" x="16" y="194" width="118" height="4" />
          <rect className="pk-line" x="16" y="206" width="86" height="4" />
          {qr(158, 208)}
        </>
      )}
      {kind === "view" && (
        <>
          <rect fill={hatch} width="200" height="250" />
          {photo("YOUR PHOTO, FULL FRAME", 100, 96)}
          <rect className="pk-band" y="176" width="200" height="74" />
          <rect className="pk-line pk-line--light" x="16" y="196" width="128" height="7" />
          <rect className="pk-line pk-line--light" x="16" y="213" width="74" height="4" />
          <g className="pk-qr pk-qr--light">
            <rect x="158" y="208" width="26" height="26" />
            <text x="171" y="224.5" textAnchor="middle">
              QR
            </text>
          </g>
        </>
      )}
      {kind === "detail" && (
        <>
          <rect className="pk-line" x="46" y="24" width="108" height="7" />
          <path fill={hatch} d="M62 170V88a38 38 0 0 1 76 0v82Z" />
          {photo("ONE", 100, 112)}
          {photo("DETAIL", 100, 124)}
          <rect className="pk-line" x="58" y="188" width="84" height="4" />
          <text className="pk-mid" x="100" y="222" textAnchor="middle">
            AED 4.2M
          </text>
          {qr(158, 208)}
        </>
      )}
      {kind === "facts" && (
        <>
          <g className="pk-plan">
            <rect x="16" y="20" width="92" height="76" />
            <path d="M62 20v44M16 64h64M88 64h20M62 78v18" />
          </g>
          <text className="pk-num" x="126" y="38">
            3
          </text>
          <rect className="pk-line" x="126" y="44" width="42" height="3" />
          <text className="pk-num" x="126" y="66">
            186
          </text>
          <rect className="pk-line" x="126" y="72" width="34" height="3" />
          <text className="pk-num" x="126" y="94">
            34
          </text>
          <rect className="pk-line" x="126" y="100" width="48" height="3" />
          <rect fill={hatch} x="16" y="118" width="168" height="62" />
          {photo("YOUR PHOTO", 100, 153)}
          <text className="pk-mid" x="16" y="222">
            AED 4.2M
          </text>
          {qr(158, 208)}
        </>
      )}
    </svg>
  );
}

function Plan({ plan, index }: { plan: PackPlan; index: number }) {
  // The third plan is the Property Launch, which has a payment link and a
  // pay button's words of its own.
  const launch = plan.id === "launch";
  const payUrl = launch ? LAUNCH_OFFER.payUrl : plan.payUrl;
  const pay = launch ? payLabel(LAUNCH_OFFER) : `Pay ${plan.price}`;

  return (
    <article
      className={plan.featured ? "pk-plan-card pk-plan-card--featured" : "pk-plan-card"}
      data-reveal
      style={after(index * 0.1)}
    >
      <h3 className="pk-plan-name">{plan.name}</h3>
      <p className="pk-plan-price">{plan.price}</p>
      <p className="pk-plan-terms">{plan.terms}</p>
      <ul>
        {plan.features.map((feature) => (
          <li key={feature}>{feature}</li>
        ))}
      </ul>
      {/* Both ways to start, as on every other service: the free step, or the
          payment for a client who is ready. Kristian, on seeing the page live
          without one: "can't pay, no button. Free review or pay". */}
      <div className="pk-plan-actions">
        {launch ? (
          <a href="/#audit" className="pk-act pk-act--free">
            Start with a free review
          </a>
        ) : (
          <a
            href={PACK_WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="pk-act pk-act--free"
          >
            Send your listing first
          </a>
        )}
        {payUrl && (
          <>
            <span className="pk-plan-or">or</span>
            <a href={payUrl} className="pk-act pk-act--pay">
              {pay} <ArrowRight className="size-4" />
            </a>
          </>
        )}
      </div>
    </article>
  );
}

/**
 * The Listing Creative Pack. It reads in the order a broker decides in: what
 * it is, what comes back, what the four ads are, what it costs, how it
 * starts, and what Adkiro will not do to a listing.
 */
function ListingCreativePackPage() {
  useReveal();

  return (
    <div className="min-h-dvh">
      <SiteHeader tone="ink" />

      <main>
        <section data-tone="ink" className="tone-ink">
          <div className="mx-auto max-w-7xl px-5 pb-16 pt-12 lg:px-8 lg:pb-24 lg:pt-20">
            <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
              <Link to="/" className="hover:text-foreground">
                Home
              </Link>{" "}
              <span aria-hidden="true">/</span> {PACK_NAME}
            </nav>
            <p className="section-kicker mt-10">{PACK_NAME}</p>
            <h1 className="mt-5 max-w-[13em] text-[clamp(2.75rem,6.6vw,6rem)] leading-[0.98]">
              Four new ads for one listing.{" "}
              <em className="text-primary">Ready in three working days.</em>
            </h1>
            <p className="mt-7 max-w-2xl text-[1.06rem] leading-relaxed text-muted-foreground lg:text-lg">
              You send the listing. We send back four ads, each built on a different idea, made from
              your own photos and with the price in plain sight. Ready for Instagram and Facebook.
            </p>
            <SendListing className="mt-9" />
            <p className="pk-note">
              The price and the delivery date come back in writing within one business day. From{" "}
              {PACK_ONE.price} for one listing. No call. Ready to start?{" "}
              <a href="#prices" className="text-primary underline underline-offset-4">
                Pay online
              </a>
              .
            </p>
          </div>
        </section>

        <section aria-labelledby="included-heading">
          <div className="mx-auto max-w-7xl px-5 pb-16 pt-20 lg:px-8 lg:pb-24 lg:pt-28">
            <div className="grid gap-4 lg:grid-cols-[18.75rem_1fr] lg:gap-10">
              <p className="section-kicker lg:pt-4" data-reveal>
                What comes back
              </p>
              <h2
                id="included-heading"
                className="text-[clamp(2.375rem,4.4vw,4rem)] leading-none"
                data-reveal
              >
                What you get for each listing
              </h2>
            </div>
            <ol className="pk-get mt-10 lg:mt-14">
              {INCLUDED.map((item, index) => (
                <li key={item.title} data-reveal style={after(index * 0.08)}>
                  <span className="pk-get-no" aria-hidden="true">
                    {index + 1}
                  </span>
                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.copy}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section aria-labelledby="ideas-heading">
          <div className="mx-auto max-w-7xl px-5 pb-20 lg:px-8 lg:pb-28">
            <div className="grid gap-4 lg:grid-cols-[18.75rem_1fr] lg:gap-10">
              <p className="section-kicker lg:pt-4" data-reveal>
                How a pack is built
              </p>
              <div>
                <h2
                  id="ideas-heading"
                  className="text-[clamp(2.375rem,4.4vw,4rem)] leading-none"
                  data-reveal
                >
                  One listing, <em className="text-primary">four ideas</em>
                </h2>
                <p
                  className="mt-5 max-w-xl text-[1.06rem] leading-relaxed text-muted-foreground"
                  data-reveal
                  style={after(0.08)}
                >
                  Not one design in four colours. Each ad makes a different case for the same
                  listing, so you find out which one your buyers answer.
                </p>
              </div>
            </div>
            <ul className="pk-ideas mt-10 lg:mt-14">
              {IDEAS.map((idea, index) => (
                <li key={idea.kind} data-reveal style={after(index * 0.1)}>
                  <IdeaSketch kind={idea.kind} />
                  <h3>{idea.title}</h3>
                  <p>{idea.copy}</p>
                </li>
              ))}
            </ul>
            <p className="mt-8 max-w-3xl text-sm leading-relaxed text-muted-foreground" data-reveal>
              The sketches show the four kinds of idea. In your pack each one is finished with your
              own photos, your price and your facts. For Dubai listings, every ad leaves a clear
              space for the permit QR code;{" "}
              <a
                href="/guides/dubai-property-ad-permit"
                className="text-primary underline underline-offset-4 hover:text-foreground"
              >
                the guide to the permit
              </a>{" "}
              explains what has to be on the ad.
            </p>
          </div>
        </section>

        <section id="prices" data-tone="ink" className="tone-ink scroll-mt-16">
          <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
            <p className="section-kicker" data-reveal>
              Prices
            </p>
            <h2 className="mt-5 text-[clamp(2.375rem,4.4vw,4rem)] leading-none" data-reveal>
              Three ways to buy. <em className="text-primary">Clear prices.</em>
            </h2>
            <div className="pk-plans mt-10 lg:mt-14">
              {PACK_PLANS.map((plan, index) => (
                <Plan key={plan.id} plan={plan} index={index} />
              ))}
            </div>
            <p className="mt-6 max-w-3xl text-sm leading-relaxed text-muted-foreground" data-reveal>
              Sending your listing first is free: the price and the delivery date come back in
              writing within one business day. If you are ready, pay now and send the photos on the
              next page. {PACK_CREDIT_NOTE} Prices are in US dollars and exclude any applicable
              taxes. {AD_SPEND_NOTE}
            </p>
          </div>
        </section>

        <section aria-labelledby="pack-steps-heading">
          <div className="mx-auto max-w-7xl px-5 pb-20 pt-20 lg:px-8 lg:pb-28 lg:pt-28">
            <div className="grid gap-4 lg:grid-cols-[18.75rem_1fr] lg:gap-10">
              <p className="section-kicker lg:pt-4" data-reveal>
                How it works
              </p>
              <h2
                id="pack-steps-heading"
                className="text-[clamp(2.375rem,4.4vw,4rem)] leading-none"
                data-reveal
              >
                Three steps, all in writing
              </h2>
            </div>
            <ol
              className="steps-line mt-10 grid gap-9 md:grid-cols-3 md:gap-12 lg:mt-14"
              data-observe
            >
              {STEPS.map((step, index) => (
                <li key={step.title} className="pt-7" data-reveal style={after(index * 0.15)}>
                  <span className="font-display text-3xl italic text-primary/50" aria-hidden="true">
                    0{index + 1}
                  </span>
                  <h3 className="mt-5 text-3xl leading-tight">{step.title}</h3>
                  <p className="mt-3 max-w-sm leading-relaxed text-muted-foreground">{step.copy}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section data-tone="ink" className="tone-oxblood">
          <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
            <p className="section-kicker" data-reveal>
              Our rules
            </p>
            <h2
              className="mt-6 max-w-[16em] text-[clamp(2rem,4.1vw,3.6rem)] leading-[1.06]"
              data-reveal
            >
              What we <em className="text-primary">never</em> do to a listing
            </h2>
            <ul className="pk-never mt-12 lg:mt-16">
              {NEVER.map((line, index) => (
                <li key={line} data-reveal style={after(index * 0.08)}>
                  {line}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section id="faq" className="scroll-mt-24">
          <div className="mx-auto grid max-w-7xl gap-8 px-5 py-20 lg:grid-cols-[18.75rem_1fr] lg:gap-10 lg:px-8 lg:py-28">
            <div>
              <p className="section-kicker" data-reveal>
                Questions
              </p>
              <h2 className="mt-4 text-[clamp(2.125rem,3.6vw,3.25rem)] leading-[1.02]" data-reveal>
                Before you send a listing
              </h2>
            </div>
            <div className="qa" data-reveal>
              {FAQS.map((faq) => (
                <details key={faq.q}>
                  <summary>
                    <h3>{faq.q}</h3>
                  </summary>
                  <p>{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section data-tone="ink" className="tone-ink">
          <div className="mx-auto max-w-7xl px-5 py-24 text-center lg:px-8 lg:py-32">
            <h2 className="text-[clamp(2.75rem,6.4vw,6rem)] leading-[0.98]" data-reveal>
              Start with <em className="text-primary">one listing</em>
            </h2>
            <p
              className="mx-auto mt-6 max-w-lg text-[1.06rem] leading-relaxed text-muted-foreground"
              data-reveal
              style={after(0.1)}
            >
              Send the link or the photos. The price and the delivery date come back in writing
              within one business day.
            </p>
            <div data-reveal style={after(0.2)}>
              <SendListing className="mt-9 justify-center" />
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
